/**
 * @file 通用弹窗状态管理 Hook
 * @description 统一管理弹窗的显示/隐藏状态和当前行数据，可在各页面复用
 */

import { useCallback, useState } from 'react';

interface UseModalResult<T> {
  /** 弹窗是否可见 */
  visible: boolean;
  /** 当前行数据 */
  currentRow: T | null;
  /** 打开弹窗 */
  open: (row?: T) => void;
  /** 关闭弹窗 */
  close: () => void;
}

/**
 * 单个弹窗状态管理 Hook
 * @template T - 当前行数据类型
 * @returns 弹窗状态和操作方法
 *
 * @example
 * const addModal = useModal();
 * const detailModal = useModal<MemberListItem>();
 *
 * // 打开弹窗
 * addModal.open();
 * detailModal.open(record);
 *
 * // 关闭弹窗
 * addModal.close();
 */
export function useModal<T = any>(): UseModalResult<T> {
  const [visible, setVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState<T | null>(null);

  const open = useCallback((row?: T) => {
    if (row !== undefined) {
      setCurrentRow(row);
    }
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    setCurrentRow(null);
  }, []);

  return {
    visible,
    currentRow,
    open,
    close,
  };
}

/**
 * 多弹窗状态管理 Hook
 * @template T - 弹窗类型枚举
 * @template R - 当前行数据类型
 * @param modalTypes - 弹窗类型数组
 * @returns 多弹窗状态和操作方法
 *
 * @example
 * const { modalState, currentRow, openModal, closeModal } = useModals<'create' | 'delete' | 'edit', RoleListItem>(
 *   ['create', 'delete', 'edit']
 * );
 *
 * // 打开弹窗
 * openModal('create');
 * openModal('edit', record);
 *
 * // 关闭弹窗
 * closeModal('edit');
 *
 * // 检查弹窗状态
 * if (modalState.create) { ... }
 */
export function useModals<T extends string, R = any>(modalTypes: T[]) {
  type ModalState = Record<T, boolean>;

  const initialState = modalTypes.reduce(
    (acc, type) => ({ ...acc, [type]: false }),
    {} as ModalState,
  );

  const [modalState, setModalState] = useState<ModalState>(initialState);
  const [currentRow, setCurrentRow] = useState<R | undefined>();

  const openModal = useCallback((type: T, row?: R) => {
    setCurrentRow(row);
    setModalState((prev) => ({ ...prev, [type]: true }));
  }, []);

  const closeModal = useCallback((type: T) => {
    setModalState((prev) => ({ ...prev, [type]: false }));
    setCurrentRow(undefined);
  }, []);

  return {
    modalState,
    currentRow,
    openModal,
    closeModal,
  };
}

export default useModal;
